import axios from "axios";
import jwt_decode from "jwt-decode";
import configJson from "../../config.json";
import { actions as authActions } from "../store/ducks/auth.duck";
import { describeRequest, flushLogoutReason, recordLogoutReason } from "./logoutReason";

const normalizedUrlBase = (configJson?.urlBase || "").replace(/\/$/, "");
const refreshUrl = normalizedUrlBase ? `${normalizedUrlBase}/auth/refresh` : "/auth/refresh";
// A refresh that never answers (the laptop slept mid-request) would otherwise
// hold every later request in this tab behind it forever.
const refreshClient = axios.create({ timeout: 20000 });
let refreshPromise = null;
let keepAliveInstalled = false;

const normalizeTokenValue = token => {
  if (typeof token !== "string") {
    return null;
  }
  const cleaned = token.replace(/['"]/g, "").trim();
  if (!cleaned) {
    return null;
  }
  const bearerMatch = cleaned.match(/^Bearer\s+(.+)$/i);
  return bearerMatch ? bearerMatch[1].trim() : cleaned;
};

const buildBearerHeader = token => {
  const normalized = normalizeTokenValue(token);
  return normalized ? `Bearer ${normalized}` : undefined;
};

const updateStoredUserDetailsToken = newToken => {
  if (!newToken) {
    return;
  }

  try {
    const storedDetails = window.localStorage.getItem("userDetails");
    if (!storedDetails) {
      return;
    }

    const parsedDetails = JSON.parse(storedDetails);
    if (parsedDetails && typeof parsedDetails === "object") {
      window.localStorage.setItem(
        "userDetails",
        JSON.stringify({ ...parsedDetails, access_token: newToken })
      );
    }
  } catch (storageError) {
    console.warn("Unable to refresh stored user details:", storageError);
  }
};

// Each tab holds its own copy of the token in redux, and redux-persist only
// reads localStorage on page load. A tab left open across a re-login (often
// frozen by the browser's sleeping tabs) still has the old expired token, so
// before logging out, check whether another tab already stored a newer one.
const readStoredToken = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem("persist:demo4-auth") || "null");
    return normalizeTokenValue(stored?.authToken);
  } catch (storageError) {
    return null;
  }
};

const adoptNewerStoredToken = (store, currentToken) => {
  const storedToken = readStoredToken();
  if (!storedToken || storedToken === currentToken || evaluateTokenState(storedToken).expired) {
    return null;
  }
  if (store) {
    store.dispatch(authActions.refreshToken(storedToken));
  }
  return storedToken;
};

const describeToken = token => {
  try {
    const { exp, iat, auth_time: authTime } = jwt_decode(token);
    return { exp, iat, authTime };
  } catch (decodeError) {
    return null;
  }
};

const handleAuthFailure = (store, reason, details) => {
  const currentToken = normalizeTokenValue(store?.getState()?.auth?.authToken);
  if (adoptNewerStoredToken(store, currentToken)) {
    // Another tab logged in again; this tab picks up that session instead of
    // wiping it out of localStorage.
    return;
  }
  recordLogoutReason(reason, { ...details, tabToken: describeToken(currentToken) });
  if (store) {
    store.dispatch(authActions.logout());
  }
  try {
    window.localStorage.removeItem("persist:demo4-auth");
    window.localStorage.removeItem("userDetails");
  } catch (storageError) {
    console.warn("Unable to clear stored auth data:", storageError);
  }
  window.location.href = "/auth/login";
};

// `reason` is what gets recorded if the API refuses the refresh.
const queueTokenRefresh = (currentToken, store, reason = "refresh_failed") => {
  if (!currentToken) {
    return Promise.reject(new Error("Missing token for refresh"));
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  const header = buildBearerHeader(currentToken);
  if (!header) {
    return Promise.reject(new Error("Invalid token for refresh"));
  }

  refreshPromise = refreshClient
    .get(refreshUrl, {
      headers: { Authorization: header },
    })
    .then(({ data }) => {
      const refreshedToken = normalizeTokenValue(data?.result?.access_token);
      if (!refreshedToken) {
        throw new Error("Missing refreshed authentication token");
      }
      if (store) {
        store.dispatch(authActions.refreshToken(refreshedToken));
      }
      updateStoredUserDetailsToken(refreshedToken);
      return refreshedToken;
    })
    .catch(error => {
      // Reloading aborts in-flight requests. A network error or temporary API
      // failure says nothing about whether the session is still valid.
      if (store && error.response?.status === 401) {
        handleAuthFailure(store, reason, describeRequest(error));
      }
      // Already dealt with here; the response interceptor must not log out again.
      error.fromSessionRefresh = true;
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const evaluateTokenState = token => {
  try {
    const decoded = jwt_decode(token);
    const now = Math.floor(Date.now() / 1000);
    const exp = Number(decoded?.exp);
    const iat = Number(decoded?.iat);

    if (exp && now >= exp) {
      return { expired: true };
    }

    if (exp && iat && exp > iat) {
      const lifetime = exp - iat;
      const refreshThreshold = exp - Math.floor(lifetime * 0.2);
      if (now >= refreshThreshold && now < exp) {
        return { shouldRefresh: true };
      }
    }
  } catch (decodeError) {
    // Ignore decode errors; the API will respond with 401 if the token is invalid.
  }

  return {};
};

export function setupAxios(axiosInstance, store) {
  // Keep tabs on the same token: when another tab logs in or refreshes, take
  // its token so this tab never runs on a stale one.
  if (store && typeof window !== "undefined") {
    window.addEventListener("storage", event => {
      if (event.key !== "persist:demo4-auth") {
        return;
      }
      const currentToken = normalizeTokenValue(store.getState()?.auth?.authToken);
      adoptNewerStoredToken(store, currentToken);
    });
  }

  axiosInstance.interceptors.request.use(
    async config => {
      if (!store) {
        return config;
      }

      const state = store.getState();
      const normalizedToken = normalizeTokenValue(state?.auth?.authToken);
      if (!normalizedToken) {
        return config;
      }

      let activeToken = normalizedToken;
      let tokenState = evaluateTokenState(activeToken);
      if (tokenState.expired) {
        const newerToken = adoptNewerStoredToken(store, activeToken);
        if (newerToken) {
          activeToken = newerToken;
          tokenState = evaluateTokenState(activeToken);
        }
      }
      if (tokenState.expired || tokenState.shouldRefresh) {
        // An expired token usually means the machine slept through the
        // refresh window. The API still renews it for a while
        // (itp-crm-api session_renewal.py), so only a refused refresh logs
        // out. A network error fails this request and keeps the session.
        activeToken = await queueTokenRefresh(
          activeToken,
          store,
          tokenState.expired ? "token_expired" : "refresh_failed"
        );
      }

      const headerValue = buildBearerHeader(activeToken);
      if (headerValue) {
        config.headers.Authorization = headerValue;
        flushLogoutReason(headerValue);
      }

      return config;
    },
    err => Promise.reject(err)
  );

  axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      if (error.fromSessionRefresh || !error.response || error.response.status !== 401) {
        return Promise.reject(error);
      }
      const config = error.config;
      const currentToken = normalizeTokenValue(store?.getState()?.auth?.authToken);
      if (!store || !currentToken || !config || config._authRetried) {
        // A 401 right after a successful refresh is the endpoint refusing this
        // request, not a dead session.
        if (!config?._authRetried) {
          handleAuthFailure(store, "401_response", describeRequest(error));
        }
        return Promise.reject(error);
      }

      // The token can die between leaving the tab and reaching the API, e.g.
      // a poll sent just before the laptop slept. Renew once and resend.
      config._authRetried = true;
      try {
        if (!adoptNewerStoredToken(store, currentToken)) {
          await queueTokenRefresh(currentToken, store, "401_response");
        }
      } catch (refreshError) {
        // A refused refresh already logged out; a network error keeps the session.
        return Promise.reject(error);
      }
      return axiosInstance(config);
    }
  );

  installSessionKeepAlive(store);
}

// Renew without waiting for a request: on wake, on focus, when the network
// returns, and once a minute. Each listener is installed once per page, since
// setupAxios runs for several axios instances.
function installSessionKeepAlive(store) {
  if (!store || keepAliveInstalled || typeof window === "undefined") {
    return;
  }
  keepAliveInstalled = true;

  const keepAlive = () => {
    const currentToken = normalizeTokenValue(store.getState()?.auth?.authToken);
    if (!currentToken) {
      return;
    }
    const tokenState = evaluateTokenState(currentToken);
    if (!tokenState.expired && !tokenState.shouldRefresh) {
      return;
    }
    if (tokenState.expired && adoptNewerStoredToken(store, currentToken)) {
      return;
    }
    queueTokenRefresh(
      currentToken,
      store,
      tokenState.expired ? "token_expired" : "refresh_failed"
    ).catch(() => {});
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      keepAlive();
    }
  });
  window.addEventListener("focus", keepAlive);
  window.addEventListener("online", keepAlive);
  window.setInterval(keepAlive, 60000);
}
